import { getIngredients } from '@bulknutrients/bulk-shared-lib/lib/sanity/ingredients';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { Container } from '@components/Container';
import { StandardLayout } from '@components/Layouts';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import type { GetStaticProps } from 'next';
import Head from 'next/head';
import type { ReactElement } from 'react';

export default function CategoryPage({
  ingredientCategories,
}: {
  ingredientCategories: { name: string; slug: string }[];
}): ReactElement {
  return (
    <>
      <Head>
        <title>Shop for supplements by Ingredient - Bulk Nutrients</title>
        <meta
          name="title"
          content="Shop for supplements by Ingredient - Bulk Nutrients"
        />
        <meta
          name="description"
          content="Browse Bulk Nutrients' extensive range of supplements via ingredient keyword. See all products sold by Bulk Nutrients categorised by ingredient"
        />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}/categories/shop-by-ingredient`}
        />
      </Head>
      <Container>
        <Breadcrumbs
          crumbs={[
            { title: 'Home', path: '/' },
            {
              title: 'Shop by Ingredient',
              path: '/categories/shop-by-ingredient',
            },
          ]}
        />
      </Container>
      <Container as="main" id="main-content">
        <div className="my-0 max-w-narrow-page justify-self-center px-0 py-5">
          <h1>Shop by Ingredient</h1>
          <Paragraph>
            Finding the right supplement with a specific ingredient can be a
            difficult task sometimes. So that's why here at Bulk Nutrients we've
            broken all our products down by ingredient. Easily searchable, all
            on the one page.
          </Paragraph>
          <Paragraph>
            Whether you're looking for products with run of the mill ingredients
            like{' '}
            <TextLink href="/categories/shop-by-ingredient/whey-protein-isolate-wpi">
              Whey Protein Isolate
            </TextLink>{' '}
            or{' '}
            <TextLink href="/categories/shop-by-ingredient/micellar-casein-milk-protein-isolate">
              Micellar Casein
            </TextLink>
            , or more obscure ones such as{' '}
            <TextLink href="/categories/shop-by-ingredient/forskolin">
              Forskolin
            </TextLink>{' '}
            or{' '}
            <TextLink href="/categories/shop-by-ingredient/arginine-alphaketoglutarate-aakg">
              Arginine Alphaketoglutarate
            </TextLink>
            , they're all listed below!
          </Paragraph>
        </div>
        <ul className="my-4 list-disc columns-1 pl-10 sm:columns-2 lg:columns-4">
          {ingredientCategories.map((category) => (
            <li key={category.slug} className="mb-1">
              <TextLink href={category.slug}>{category.name}</TextLink>
            </li>
          ))}
        </ul>
      </Container>
    </>
  );
}

CategoryPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const ingredientCategories = await getIngredients();

  return {
    props: {
      ingredientCategories,
    },
  };
};
